import { useEffect } from "react";
import { useClaimPoints } from "../../hooks";
import { useTransactionStore } from "../../stores/transactionStore";
import { LoadingButton } from "../shared/LoadingButton";

interface ClaimSectionProps {
	points: bigint;
	formattedPoints: string;
	formattedVoid: string;
	hasClaimed: boolean;
	onClaimed?: () => void;
}

export function ClaimSection({
	points,
	formattedPoints,
	formattedVoid,
	hasClaimed,
	onClaimed,
}: ClaimSectionProps) {
	const { claimPoints, isPending, isSuccess, reset } = useClaimPoints();
	const addToast = useTransactionStore((s) => s.addToast);

	const hasPoints = points > 0n;
	const displayPoints = Number.parseFloat(formattedPoints).toFixed(4);
	const displayVoid = Number.parseFloat(formattedVoid).toFixed(2);

	async function handleClaim(): Promise<void> {
		try {
			reset();
			await claimPoints();
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			const short = msg.includes("User rejected")
				? "Transaction rejected"
				: msg.length > 120
					? `${msg.slice(0, 120)}...`
					: msg;
			addToast({ type: "error", title: "Claim Failed", message: short });
		}
	}

	useEffect(() => {
		if (isSuccess) {
			addToast({
				type: "success",
				title: "Points Claimed",
				message: `Successfully claimed ${displayVoid} VOID tokens.`,
			});
			onClaimed?.();
		}
	}, [isSuccess, addToast, displayVoid, onClaimed]);

	if (hasClaimed) {
		return (
			<div className="claim-bar claim-bar--claimed">
				<span className="claim-bar__status">✓ Points claimed — converted to VOID tokens</span>
			</div>
		);
	}

	if (!hasPoints) {
		return (
			<div className="claim-bar claim-bar--empty">
				<span className="claim-bar__status">No points to claim — complete missions to earn</span>
			</div>
		);
	}

	return (
		<div className="claim-bar">
			<div className="claim-bar__readouts">
				<div className="claim-bar__readout">
					<span className="claim-bar__label">Points</span>
					<span className="claim-bar__value">{displayPoints}</span>
				</div>
				<span className="claim-bar__arrow">→</span>
				<div className="claim-bar__readout">
					<span className="claim-bar__label">VOID</span>
					<span className="claim-bar__value claim-bar__value--accent">{displayVoid}</span>
				</div>
			</div>
			<LoadingButton onClick={handleClaim} loading={isPending} disabled={!hasPoints}>
				Claim VOID
			</LoadingButton>
		</div>
	);
}
